import { debounce as _debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFetchTargetingValues } from '../../../apis/targetingManagerApi/targetingManagerApi';
import type { TargetingDefinition, TargetingValue } from '../../../models';
import type { AdFormValues } from '../../AdForm/adFormik';
import type { DropdownOption } from '../../Dropdown';
import { DEBOUNCE_DURATION_MS } from '../../hooks/constants';
import { DIMENSIONS_DOES_NOT_SUPPORT_EMPTY_SEARCH, GeoDimension } from '../constants';
import { DEFAULT_TARGETING_PAGINATION_LIMIT } from './constants';
import { createTargetingValueDropdownOption, prefixDisplayNameAsAppropriate } from './utils';

export type UseTargetingValuesDropdownReturn = {
  options: DropdownOption[];
  loading: boolean;
  loadingMore: boolean;
  error?: string;
  search: (searchTerm: string) => void;
  reset: () => void;
  clearSearch: () => void;
  onNext: () => Promise<void>;
  hasMore: boolean;
  searchTerm: string;
  filterOption?: () => boolean;
  supportEmptySearch: boolean;
  searching: boolean;
};
type SearchState = {
  searchTerm: string;
  searching: boolean;
  offset: number;
  localError?: string;
  pendingReset: boolean;
};
/**
 * Hook for managing targeting values dropdown with search, pagination, and loading states.
 * @param dimensionId - ID of the targeting dimension (used to determine if empty search is supported)
 * @param newTargeting - Current Formik newTargeting field value containing category and dimension
 * @param countryIdsForFilteringValues - Array of country targeting values for filtering
 * @param publishersForFilteringValues - Array of publisher targeting values for filtering
 */
const useTargetingValuesDropdown = (
  dimensionId?: string,
  newTargeting?: AdFormValues['newTargeting'],
  countryIdsForFilteringValues?: string[],
  publishersForFilteringValues?: TargetingValue[]
): UseTargetingValuesDropdownReturn => {
  // Extract stable IDs to prevent unnecessary recalculations
  const categoryId = newTargeting?.category?.id;
  const dimensionIdFromTargeting = newTargeting?.dimension?.id;

  const [searchState, setSearchState] = useState<SearchState>({
    searchTerm: '',
    searching: false,
    offset: 0,
    localError: undefined,
    pendingReset: false,
  });
  const prevConfigRef = useRef({
    categoryId,
    dimensionId: dimensionIdFromTargeting,
  });

  const supportEmptySearch = useMemo(
    () => !DIMENSIONS_DOES_NOT_SUPPORT_EMPTY_SEARCH.includes((dimensionId ?? '').toLowerCase()),
    [dimensionId]
  );

  const { data, loading, loadingMore, error, refetch, reset: resetData, hasMore } = useFetchTargetingValues({
    categoryGuid: categoryId ?? '',
    dimensionId: dimensionIdFromTargeting ?? '',
    searchTerm: searchState.searchTerm || null,
    offset: searchState.offset,
    limit: DEFAULT_TARGETING_PAGINATION_LIMIT,
    lineItemCountryIds: countryIdsForFilteringValues,
    lineItemPublishers: publishersForFilteringValues,
    enabled: !!categoryId && !!dimensionIdFromTargeting,
    lazy: true,
    refetchOnMount: false,
    valuesFilterIds:
      dimensionIdFromTargeting === GeoDimension.Country && countryIdsForFilteringValues
        ? countryIdsForFilteringValues
        : undefined,
  });

  // Memoize dimension ID string to prevent unnecessary recalculations
  const dimensionIdStr = useMemo(() => dimensionIdFromTargeting ?? '', [dimensionIdFromTargeting]);

  const options = useMemo(() => {
    if (!data?.nodes?.length || !dimensionIdStr) return [];
    const targetingValuesData = prefixDisplayNameAsAppropriate(data.nodes, dimensionIdStr);
    return targetingValuesData.map((node: TargetingDefinition) =>
      createTargetingValueDropdownOption(node, dimensionIdStr)
    );
  }, [data?.nodes, dimensionIdStr]);

  // Check if category or dimension has changed since last render
  // NOTE: This must NOT be memoized because it reads from prevConfigRef.current,
  // which is updated in the effect below. Memoizing would cause stale cached values
  // to trigger unnecessary resets during pagination/loadingMore.
  const configChanged =
    prevConfigRef.current.categoryId !== categoryId || prevConfigRef.current.dimensionId !== dimensionIdFromTargeting;

  useEffect(() => {
    if (!categoryId || !dimensionIdFromTargeting) return;
    if (configChanged) {
      prevConfigRef.current = {
        categoryId,
        dimensionId: dimensionIdFromTargeting,
      };
      resetData();
      setSearchState({ searchTerm: '', searching: false, offset: 0, localError: undefined, pendingReset: false });
      refetch(false);
    }
  }, [categoryId, dimensionIdFromTargeting, configChanged, refetch, resetData]);

  // Handle pending reset: refetch after offset has been reset to 0
  useEffect(() => {
    if (searchState.pendingReset && searchState.offset === 0 && categoryId && dimensionIdFromTargeting) {
      setSearchState((prev) => ({ ...prev, pendingReset: false }));
      refetch(false);
    }
  }, [searchState.pendingReset, searchState.offset, categoryId, dimensionIdFromTargeting, refetch]);

  // Clear searching flag only when loading transitions from true → false (fetch completed).
  // Previously this fired whenever `!loading && searching`, which immediately cleared the
  // flag on every keystroke (because loading is still false before the debounced fetch starts).
  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    if (prevLoadingRef.current && !loading && searchState.searching) {
      setSearchState((prev) => ({ ...prev, searching: false }));
    }
    prevLoadingRef.current = loading;
  }, [loading, searchState.searching]);
  const onNext = useCallback(async (): Promise<void> => {
    if (loading || loadingMore) return;
    if (!hasMore) {
      setSearchState((prev) => ({ ...prev, localError: 'Error: no more to load' }));
      return;
    }
    try {
      const newOffset = data?.nodes?.length ?? 0;
      setSearchState((prev) => ({ ...prev, offset: newOffset, localError: undefined }));
      await refetch(true);
    } catch (e) {
      setSearchState((prev) => ({
        ...prev,
        localError: `Error loading more: ${e instanceof Error ? e.message : String(e)}`,
      }));
    }
  }, [loading, loadingMore, hasMore, data?.nodes?.length, refetch]);
  // Use refs to access latest values in debounced function without recreating it
  const categoryIdRef = useRef(categoryId);
  const dimensionIdRef = useRef(dimensionIdFromTargeting);
  const supportEmptySearchRef = useRef(supportEmptySearch);
  const resetDataRef = useRef(resetData);

  useEffect(() => {
    categoryIdRef.current = categoryId;
    dimensionIdRef.current = dimensionIdFromTargeting;
    supportEmptySearchRef.current = supportEmptySearch;
    resetDataRef.current = resetData;
  }, [categoryId, dimensionIdFromTargeting, supportEmptySearch, resetData]);

  // Create debounced search once and reuse it to avoid canceling pending debounces unnecessarily
  const debouncedSearch = useMemo(
    () =>
      _debounce(async (searchTerm: string | null): Promise<void> => {
        try {
          if (!categoryIdRef.current || !dimensionIdRef.current) return;
          if (!supportEmptySearchRef.current && !searchTerm) {
            setSearchState((prev) => ({ ...prev, searching: false }));
            return;
          }
          // Reset offset, then trigger refetch via pendingReset effect.
          // Do NOT call resetData() here — keep the stale options visible so the
          // dropdown doesn't collapse while the search fetch is in flight.
          // When the fetch completes, useFetch replaces data entirely (shouldAppend=false).
          setSearchState((prev) => ({ ...prev, offset: 0, localError: undefined, pendingReset: true }));
        } catch (e) {
          setSearchState((prev) => ({
            ...prev,
            searching: false,
            localError: `Search error: ${e instanceof Error ? e.message : String(e)}`,
          }));
        }
      }, DEBOUNCE_DURATION_MS),
    [] // Empty deps - function is stable, uses refs for latest values
  );

  // Cleanup: cancel any pending debounced search calls only on unmount
  useEffect(() => {
    return (): void => {
      debouncedSearch.cancel?.();
    };
  }, [debouncedSearch]);

  const search = useCallback(
    (term: string): void => {
      setSearchState((prev) => ({ ...prev, searchTerm: term, searching: true }));
      debouncedSearch(term);
    },
    [debouncedSearch]
  );

  const reset = useCallback((): void => {
    // Reset state and clear data; the pendingReset flag will trigger refetch
    // after state update propagates to ensure URL uses offset 0
    setSearchState({
      searchTerm: '',
      searching: false,
      offset: 0,
      localError: undefined,
      pendingReset: true,
    });
    resetData();
  }, [resetData]);

  // Clear search state without destroying cached data.
  // If a search was active, reset data and refetch to restore the unfiltered list.
  // If no search was active, do nothing — the cached pages remain intact.
  const clearSearch = useCallback((): void => {
    debouncedSearch.cancel?.();
    if (searchState.searchTerm) {
      setSearchState({
        searchTerm: '',
        searching: false,
        offset: 0,
        localError: undefined,
        pendingReset: true,
      });
      resetData();
    }
  }, [searchState.searchTerm, resetData, debouncedSearch]);

  // Memoize filterOption to prevent unnecessary recreations
  const filterOption = useCallback(() => true, []);

  // Memoize error to prevent unnecessary recalculations
  const computedError = useMemo(() => searchState.localError ?? error ?? undefined, [searchState.localError, error]);

  // Memoize return object to prevent unnecessary rerenders in consuming components
  return useMemo(
    () => ({
      supportEmptySearch,
      options,
      loading,
      loadingMore,
      error: computedError,
      search,
      searchTerm: searchState.searchTerm,
      reset,
      clearSearch,
      onNext,
      filterOption,
      hasMore,
      searching: searchState.searching,
    }),
    [
      supportEmptySearch,
      options,
      loading,
      loadingMore,
      computedError,
      search,
      searchState.searchTerm,
      reset,
      clearSearch,
      onNext,
      filterOption,
      hasMore,
      searchState.searching,
    ]
  );
};
export default useTargetingValuesDropdown;
